/**
 * HomeHero — condensed dashboard band (zone 1 of the SUPER AWENI home).
 *
 * Pattern: "complications row" (Apple Watch / iOS 17 Standby / Material 3
 * dashboard cards). Renders SYNCHRONOUSLY at mount with cached data only;
 * click each chip to open the full widget for deep details.
 *
 * Layout:
 *   [📍 City]                                          [↻]
 *   As-salaamu alaykum, {name}
 *
 *   ┌────────────────┐ ┌─────────┐ ┌──────────┐ ┌──────────────┐
 *   │ 📅 Wed May 6   │ │ 🕐 06:02│ │ 🌤 18°   │ │ 🕌 Sunrise   │
 *   │ 21 Dhū al-Q.   │ │ tz      │ │ feels 16°│ │ in 50 min    │
 *   └────────────────┘ └─────────┘ └──────────┘ └──────────────┘
 *      → #/yawmi/calendar → TimeModal  → /weather   → /prayer
 *
 * Each chip is a single source of truth — no duplicate cards in the feed
 * below for date/time/weather/prayer. Tap = drill into deep details.
 *
 * MUST stay synchronous + side-effect-free (per CLAUDE.md "Home Page
 * Anti-Regression"): no fetch, no geolocation prompt, no heavy compute.
 * Time tick is a single per-minute interval, cleaned up on unmount.
 */

import type { AweniContext } from '../../types';
import { prayerTimeService } from '../../services/PrayerTimeService';
import type { LocationService } from '../../services/LocationService';
import type { WeatherService } from '../../services/WeatherService';
import { gregorianToHijri, formatHijri } from '../../services/HijriDate';
import { TimeModal } from './TimeModal';
import { LocationHeader } from '../../shell/LocationHeader';
import { swr } from '../../services/swr';

/** localStorage keys shared with HabitTab. */
const HERO_HABIT_LOGS_KEY = 'aweni_yawmi_habit_logs';

/** Prayer → habit ID mapping (mirrors PrayerScheduleStrip). */
const HERO_PRAYER_HABIT_ID: Record<string, string> = {
  fajr: 'p_fajr',
  dhuhr: 'p_dhuhr',
  asr: 'p_asr',
  maghrib: 'p_maghrib',
  isha: 'p_isha',
};

/** Read today's done-set from localStorage (graceful on failure). */
function heroReadTodayDoneSet(): Set<string> {
  const today = new Date().toISOString().slice(0, 10);
  const done = new Set<string>();
  try {
    const logs: Array<{ habitId: string; date: string; count: number }> =
      JSON.parse(localStorage.getItem(HERO_HABIT_LOGS_KEY) || '[]');
    for (const l of logs) {
      if (l.date === today && l.count > 0) done.add(l.habitId);
    }
  } catch { /* ignore */ }
  return done;
}

interface TrendPayload {
  ok?: boolean;
  data?: {
    user_id?: number;
    week_start?: string;
    generated_at?: number;
    highlights?: string[];
    dimensions?: Record<string, unknown>;
  };
  // Some shapes return the data at top level (depending on ResponseFormatter)
  highlights?: string[];
}

async function fetchTrendRadar(): Promise<TrendPayload | null> {
  try {
    const r = await fetch('/aweni/api/v1/trend-radar', {
      credentials: 'include',
      cache: 'no-store',
    });
    if (!r.ok) return null;
    const data = (await r.json()) as TrendPayload;
    return data;
  } catch {
    return null;
  }
}

export interface HomeHeroDeps {
  ctx: AweniContext;
  locationService: LocationService;
  weatherService: WeatherService;
  onChooseCity: () => void;
  onTapPrayer: () => void;
  onTapSettings: () => void;
}

export class HomeHero {
  private root: HTMLElement | null = null;
  private deps: HomeHeroDeps;
  private timeTick: ReturnType<typeof setInterval> | null = null;
  private locationHeader: LocationHeader | null = null;
  /** T3-14 — interval that rotates trend-radar highlights every 6s. */
  private trendRotator: ReturnType<typeof setInterval> | null = null;
  /** Unsubscribe handle for prayerTimeService.onChange — fixes the
   *  "Dhuhr at 13:27 vs strip 13:32" mismatch where the hero rendered
   *  once with stale data and never updated when the server response
   *  refined the calculation. */
  private prayerUnsub: (() => void) | null = null;

  constructor(deps: HomeHeroDeps) {
    this.deps = deps;
  }

  /** Render synchronously into the given parent element. */
  mount(parent: HTMLElement): void {
    const root = document.createElement('section');
    root.className = 'aweni-hero';
    root.setAttribute('aria-label', this.deps.ctx.i18n.t('home_hero_label') || 'Your daily summary');
    this.root = root;
    parent.appendChild(root);
    this.render();
    this.startTimeTick();

    // Re-render the hero whenever the prayer service updates (server
    // response lands, method/madhab changes, force-local toggle, etc.)
    // Without this, the chip can show a stale prayer time while the
    // strip below shows the refined value — the bug surfaced as
    // "Dhuhr at 13:27" in the chip vs "DHUHR 13:32" in the strip.
    this.prayerUnsub = prayerTimeService.onChange(() => {
      this.render();
    });

    // Mount the unified LocationHeader into the dedicated slot. It owns all
    // location interaction (auto-detect + favorites + search) and notifies
    // every consuming widget through LocationService.onChange().
    const slot = root.querySelector<HTMLElement>('[data-loc-header-slot]');
    if (slot) {
      this.locationHeader = new LocationHeader({
        ctx: this.deps.ctx,
        collapsible: true,
        onChange: () => {
          // Re-render hero so chips reflect the new city/weather/prayer
          // immediately. Widgets listen to LocationService.onChange()
          // independently — this just keeps the hero in sync.
          this.render();
        },
      });
      this.locationHeader.mount(slot);
    }
  }

  /** Re-render the hero (called when location/weather/prayer data changes). */
  render(): void {
    if (!this.root) return;
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);
    const isRtl = this.deps.ctx.i18n.dir === 'rtl';
    const userName = this.deps.ctx.auth.user?.name?.split(' ')[0] || '';
    const isGuest = !!this.deps.ctx.auth.user?.is_guest;
    const locale = this.deps.ctx.i18n.locale;

    const loc = this.deps.locationService.getState();
    const weather = this.deps.weatherService.getData();
    const hasLocation = loc.lat !== null && loc.lng !== null;

    const greeting = isGuest
      ? t('home_hero_greeting_guest')
      : (userName ? t('home_hero_greeting_named', { name: userName }) : t('home_hero_greeting_anon'));

    // Compute chip data.
    const date = this._dateChipData(locale);
    const time = this._timeChipData(locale);
    const wx = this._weatherChipData(weather, t);

    // Preserve mounted LocationHeader between renders — only set its slot
    // markup once. The hero re-renders frequently (per-minute time tick,
    // weather refresh) but the header should persist its expanded state
    // and listeners across those re-renders.
    const existingSlot = this.root.querySelector<HTMLElement>('[data-loc-header-slot]');
    const preserveLocHeader = existingSlot && this.locationHeader;

    this.root.innerHTML = `
      <h1 class="aweni-hero-greeting" dir="${isRtl ? 'rtl' : 'ltr'}">${escHtml(greeting)}</h1>
      <div data-trend-highlight class="aweni-hero-trend" hidden></div>
      <div data-loc-header-slot class="aweni-hero-loc-slot"></div>

      <div class="aweni-hero-chips" role="group" aria-label="${t('home_hero_chips_aria') || 'Today summary'}">
        <button type="button" class="aweni-hero-chip" data-action="open-calendar"
                aria-label="${escAttr(date.aria)}">
          <span class="aweni-hero-chip-icon">📅</span>
          <span class="aweni-hero-chip-body">
            <span class="aweni-hero-chip-primary">${escHtml(date.primary)}</span>
            <span class="aweni-hero-chip-secondary">${escHtml(date.secondary)}</span>
          </span>
        </button>

        <button type="button" class="aweni-hero-chip" data-action="open-time"
                aria-label="${escAttr(time.aria)}">
          <span class="aweni-hero-chip-icon">🕐</span>
          <span class="aweni-hero-chip-body">
            <span class="aweni-hero-chip-primary" data-tick="time">${escHtml(time.primary)}</span>
            <span class="aweni-hero-chip-secondary">${escHtml(time.secondary)}</span>
          </span>
        </button>

        <a class="aweni-hero-chip" href="#/weather" data-action="open-weather"
           aria-label="${escAttr(wx.aria)}">
          <span class="aweni-hero-chip-icon">${wx.icon}</span>
          <span class="aweni-hero-chip-body">
            <span class="aweni-hero-chip-primary">${escHtml(wx.primary)}</span>
            <span class="aweni-hero-chip-secondary">${escHtml(wx.secondary)}</span>
          </span>
        </a>
      </div>

      <div class="aweni-hero-prayer-strip" role="region"
           aria-label="${escAttr(t('prayer_banner_label') || 'Prayer schedule')}">
        ${this._prayerHeroStripHtml(hasLocation, t, locale)}
      </div>
    `;

    // Re-attach the existing LocationHeader into the new slot if we had one.
    if (preserveLocHeader) {
      const newSlot = this.root.querySelector<HTMLElement>('[data-loc-header-slot]');
      // The header's own root is detached when the parent innerHTML is
      // overwritten; re-mount it into the new slot. Since LocationHeader
      // owns its DOM and listeners, we tear it down and re-create instead
      // of reaching into private state.
      this.locationHeader?.unmount();
      if (newSlot) {
        this.locationHeader = new LocationHeader({
          ctx: this.deps.ctx,
          collapsible: true,
          onChange: () => this.render(),
        });
        this.locationHeader.mount(newSlot);
      }
    }

    this._wireHandlers();
  }

  private _wireHandlers(): void {
    if (!this.root) return;
    const root = this.root;

    // Date chip → Yawmi calendar deep-link (single source of truth per
    // user directive 2026-05-16: "AWENI calendar should be redirected to
    // Yawmi calendar to avoid 2 different checks each time"). The Yawmi
    // calendar tab parses `?date=YYYY-MM-DD` and opens on that month + day.
    // Per Searchov 2026-05-16 deep-research: this is a deep-link (chip-tap
    // pattern, iOS Apple Watch Complication standard) not a tab auto-switch
    // (HIG anti-pattern). One canonical calendar surface owns all Hijri
    // dates, eliminating the prior "Home shows 14, Yawmi shows 28" drift.
    root.querySelector('[data-action="open-calendar"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      try {
        location.hash = `#/yawmi/calendar?date=${y}-${m}-${d}`;
      } catch (err) {
        console.warn('[HomeHero] Yawmi calendar navigation failed:', err);
      }
    });

    // Time chip → TimeModal (overlay with TimeWidget expanded)
    root.querySelector('[data-action="open-time"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      try {
        new TimeModal(this.deps.ctx).show();
      } catch (err) {
        console.warn('[HomeHero] TimeModal failed:', err);
      }
    });

    // Weather chip → /weather (native <a href>, also trigger analytics hook later)
    root.querySelector('[data-action="open-weather"]')?.addEventListener('click', () => {
      // No preventDefault — let the hash route fire
    });

    // T3-14 — TrendRadar highlights strip below the greeting. Renders
    // synchronously from cache (if any), then revalidates via SWR. Skipped
    // for guests (the endpoint requires JWT and the values are per-user).
    this._mountTrendHighlights();
  }

  /** T3-14 — render up to 3 weekly highlights, rotating every 6s. */
  private _mountTrendHighlights(): void {
    const root = this.root;
    if (!root) return;
    const slot = root.querySelector<HTMLElement>('[data-trend-highlight]');
    if (!slot) return;
    if (this.deps.ctx.auth.user?.is_guest) {
      slot.hidden = true;
      return;
    }

    const renderHighlights = (highlights: string[]): void => {
      if (!highlights.length) {
        slot.hidden = true;
        return;
      }
      slot.hidden = false;
      slot.innerHTML = `
        <span class="aweni-hero-trend-icon" aria-hidden="true">✨</span>
        <span class="aweni-hero-trend-text" data-trend-line>${escHtml(highlights[0])}</span>
      `;
      // Stop any prior rotator before starting a new one.
      if (this.trendRotator !== null) { clearInterval(this.trendRotator); this.trendRotator = null; }
      if (highlights.length > 1) {
        let idx = 0;
        this.trendRotator = setInterval(() => {
          idx = (idx + 1) % highlights.length;
          const line = root.querySelector<HTMLElement>('[data-trend-line]');
          if (line) {
            line.style.opacity = '0';
            setTimeout(() => {
              line.textContent = highlights[idx] || '';
              line.style.opacity = '1';
            }, 180);
          }
        }, 6000);
      }
    };

    void swr<TrendPayload | null>(
      'trend_radar_v1',
      () => fetchTrendRadar(),
      {
        freshTtlMs: 5 * 60 * 1000,
        staleTtlMs: 60 * 60 * 1000,
        onUpdate: (data) => {
          const highlights = data?.data?.highlights ?? data?.highlights ?? [];
          renderHighlights(highlights);
        },
      },
    ).then((data) => {
      const highlights = data?.data?.highlights ?? data?.highlights ?? [];
      renderHighlights(highlights);
    }).catch(() => { /* silent — feed simply doesn't render the strip */ });
  }

  /** Per-minute tick that updates only the time chip's primary text — no
   *  full re-render to avoid touching the rest of the hero. */
  private startTimeTick(): void {
    if (this.timeTick !== null) return;
    const update = (): void => {
      const el = this.root?.querySelector<HTMLElement>('[data-tick="time"]');
      if (!el) return;
      const locale = this.deps.ctx.i18n.locale;
      try {
        el.textContent = new Intl.DateTimeFormat(locale, {
          hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(new Date());
      } catch {
        el.textContent = new Date().toLocaleTimeString(undefined, {
          hour: '2-digit', minute: '2-digit',
        });
      }
    };
    // Align with next minute boundary, then tick every 60s.
    const msToNextMinute = 60_000 - (Date.now() % 60_000);
    setTimeout(() => {
      update();
      this.timeTick = setInterval(update, 60_000);
    }, msToNextMinute);
  }

  unmount(): void {
    if (this.timeTick !== null) {
      clearInterval(this.timeTick);
      this.timeTick = null;
    }
    if (this.trendRotator !== null) {
      clearInterval(this.trendRotator);
      this.trendRotator = null;
    }
    if (this.prayerUnsub) {
      this.prayerUnsub();
      this.prayerUnsub = null;
    }
    this.locationHeader?.unmount();
    this.locationHeader = null;
    this.root?.remove();
    this.root = null;
  }

  // ---------- chip data computation ---------------------------------------

  private _dateChipData(locale: string): { primary: string; secondary: string; aria: string } {
    const now = new Date();
    let primary = '';
    try {
      primary = new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }).format(now);
    } catch {
      primary = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
    const hijri = formatHijri(gregorianToHijri(now), locale);
    return {
      primary,
      secondary: hijri,
      aria: this.deps.ctx.i18n.t('home_hero_date_aria') || 'Open calendar',
    };
  }

  private _timeChipData(locale: string): { primary: string; secondary: string; aria: string } {
    const now = new Date();
    let primary = '';
    try {
      primary = new Intl.DateTimeFormat(locale, {
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).format(now);
    } catch {
      primary = now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }
    let tz = '';
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* */ }
    const tzShort = tz ? tz.split('/').pop() || tz : '';
    return {
      primary,
      secondary: tzShort,
      aria: this.deps.ctx.i18n.t('home_hero_time_aria') || 'Open time, world clock, stopwatch',
    };
  }

  private _weatherChipData(
    weather: ReturnType<WeatherService['getData']>,
    t: (k: string, p?: Record<string, string | number>) => string,
  ): { primary: string; secondary: string; icon: string; aria: string } {
    if (!weather || typeof weather.temperature !== 'number') {
      return {
        primary: t('home_hero_weather_none') || '—',
        secondary: t('weather_loading') || '',
        icon: '🌤',
        aria: t('home_hero_weather_aria') || 'Open weather',
      };
    }
    const temp = `${Math.round(weather.temperature)}°`;
    const feels = typeof weather.feelsLike === 'number'
      ? `${t('weather_feels_like') || 'feels'} ${Math.round(weather.feelsLike)}°`
      : '';
    return {
      primary: temp,
      secondary: feels,
      icon: this._weatherEmoji(weather),
      aria: t('home_hero_weather_aria') || 'Open weather',
    };
  }

  /** Map weather data to a single-emoji glyph. */
  private _weatherEmoji(weather: ReturnType<WeatherService['getData']>): string {
    if (!weather) return '🌤';
    if (weather.rain && weather.rain > 0) return '🌧️';
    if ((weather.precipitationProbability || 0) > 60) return '🌦️';
    if ((weather.humidity || 0) > 80) return '☁️';
    if (typeof weather.temperature === 'number' && weather.temperature > 30) return '☀️';
    if (typeof weather.temperature === 'number' && weather.temperature < 0) return '❄️';
    return '🌤️';
  }

  /**
   * Renders the 5-prayer Hero strip HTML (Fajr, Dhuhr, Asr, Maghrib, Isha).
   * Each prayer is a tappable <a href="#/prayer"> with:
   *   - Name label (abbreviated, uppercase)
   *   - 24h time
   *   - ✓ done-marker when the prayer habit is logged today
   *   - Countdown badge on the next prayer
   * States: is-past (faded), is-current (accent border), is-next (accent bg).
   * Returns empty string when no location / prayer data available.
   */
  private _prayerHeroStripHtml(
    hasLocation: boolean,
    t: (k: string, p?: Record<string, string | number>) => string,
    locale: string,
  ): string {
    if (!hasLocation) {
      return `<p class="aweni-hero-prayer-no-loc">${escHtml(t('home_hero_choose_city') || 'Set city for prayer times')}</p>`;
    }

    const now = new Date();
    let strip;
    try {
      strip = prayerTimeService.getTodayFivePrayerStrip(now);
    } catch { strip = null; }

    if (!strip || !strip.length) {
      return `<p class="aweni-hero-prayer-no-loc">${escHtml(t('home_hero_prayer_none') || '—')}</p>`;
    }

    const doneSet = heroReadTodayDoneSet();

    const fmt = (d: Date): string => {
      try {
        return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
      } catch { return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }); }
    };

    const countdownText = (time: Date): string => {
      const mins = Math.max(0, Math.round((time.getTime() - now.getTime()) / 60_000));
      if (mins < 1) return t('prayer_in_moment') || 'now';
      if (mins < 60) return (t('prayer_in_min') || 'in {m}min').replace('{m}', String(mins));
      const h = Math.floor(mins / 60), m = mins % 60;
      return m === 0
        ? (t('prayer_in_h') || 'in {h}h').replace('{h}', String(h))
        : (t('prayer_in_hm') || 'in {h}h {m}min').replace('{h}', String(h)).replace('{m}', String(m));
    };

    // "Current" = most-recently-passed obligatory prayer (not the next one).
    const pastEntries = strip.filter(e => e.isPast && !e.isNext);
    const currentName = pastEntries.length ? pastEntries[pastEntries.length - 1].name : null;

    const chips = strip.map(entry => {
      const habitId = HERO_PRAYER_HABIT_ID[entry.name];
      const isDone = habitId ? doneSet.has(habitId) : false;
      const name = prayerTimeService.getPrayerName(entry.name, locale as 'en');
      const timeStr = fmt(entry.time);
      const isCurrent = currentName === entry.name;

      let cls = 'aweni-hps-chip';
      if (entry.isPast && !isCurrent) cls += ' is-past';
      if (isCurrent) cls += ' is-current';
      if (entry.isNext) cls += ' is-next';

      const doneMarker = isDone
        ? `<span class="aweni-hps-done" aria-label="${escAttr(t('prayer_done_marker') || 'done')}">✓</span>`
        : '';

      const badge = entry.isNext
        ? `<span class="aweni-hps-countdown" role="timer" aria-live="off">${escHtml(countdownText(entry.time))}</span>`
        : '';

      let ariaLabel = `${name} ${timeStr}`;
      if (isDone) ariaLabel += `, ${t('prayer_done_marker') || 'done'}`;
      if (entry.isNext) ariaLabel += `, ${countdownText(entry.time)}`;

      return `<a class="${escAttr(cls)}" href="#/prayer" role="listitem"
           aria-current="${entry.isNext ? 'true' : 'false'}"
           aria-label="${escAttr(ariaLabel)}">
        <span class="aweni-hps-name">${escHtml(name)}</span>
        <span class="aweni-hps-time">${escHtml(timeStr)}</span>
        ${doneMarker}${badge}
      </a>`;
    });

    const qiblaLabel = t('home_hero_qibla') || 'Qibla';
    const settingsLabel = t('home_hero_prayer_settings') || 'Prayer settings';

    return `
      <div class="aweni-hps-outer">
        <div class="aweni-hps-row" role="list">${chips.join('')}</div>
        <div class="aweni-hps-actions">
          <a class="aweni-hps-action-btn" href="#/hisni?tab=qibla"
             aria-label="${escAttr(qiblaLabel)}" title="${escAttr(qiblaLabel)}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                       fill="currentColor" opacity="0.15"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="12" x2="14" y2="14"/>
            </svg>
          </a>
          <a class="aweni-hps-action-btn" href="#/more/settings?section=prayer"
             aria-label="${escAttr(settingsLabel)}" title="${escAttr(settingsLabel)}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="4" y1="6" x2="20" y2="6"/>
              <line x1="4" y1="12" x2="20" y2="12"/>
              <line x1="4" y1="18" x2="20" y2="18"/>
              <circle cx="8" cy="6" r="2" fill="currentColor"/>
              <circle cx="16" cy="12" r="2" fill="currentColor"/>
              <circle cx="10" cy="18" r="2" fill="currentColor"/>
            </svg>
          </a>
        </div>
      </div>`;
  }
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
